import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useUpdateWithNestedListMutation } from 'store/services/flow/agendaRecords';
import type { AttributeType, FlattenedJoinedAgendaRecord, GetAgendaRecordsRes, ValidationResult, ViewParameter } from 'types';
import type { AgendaRecordItem } from 'types/records';
import type { TableRef } from 'components/common/Table/Table';
import { FormValidationException } from 'utils/error/FormValidationException';
import { useAgendaTableActions } from './useAgendaTableActions';
import { useAgendaTableCalculation } from './useAgendaTableCalculation';
import {
  buildJoinedListingDataSchema,
  buildJoinedSaveRequests,
  canEditJoinedCell,
  type DroppedJoinedAttribute,
  emptyIdentity,
  joinedIdentityByRowId,
  joinedParentCellPatches,
  JoinedValueConflictError,
  splitJoinedCalculatePayload,
} from './joinedListing';

const sameCellValue = (left: unknown, right: unknown) => JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

interface UseJoinedListingColumnsProps {
  agendaId: string;
  viewParameters: ViewParameter[];
  records?: FlattenedJoinedAgendaRecord[];
  recordsResult: GetAgendaRecordsRes<AgendaRecordItem[]>;
  isJoinedListing: boolean;
  setValue?: (name: string, value: string[], options?: object) => void;
  triggerForm?: (name: string) => void;
}

export const useJoinedListingColumns = ({
  agendaId,
  viewParameters,
  records,
  recordsResult,
  isJoinedListing,
  setValue,
  triggerForm,
}: UseJoinedListingColumnsProps) => {
  const { t } = useTranslation();
  const [updateWithNestedList, { isLoading: isJoinedSaving }] = useUpdateWithNestedListMutation();

  const dataSchema = useMemo(() => buildJoinedListingDataSchema(viewParameters), [viewParameters]);
  const pageIdentityByRowId = useMemo(() => joinedIdentityByRowId(records), [records]);
  const retainedIdentityRef = useRef(new Map(pageIdentityByRowId));
  const retainedAgendaIdRef = useRef(agendaId);

  // Written during render so this pass can address dirty rows that have left the page.
  // An effect would publish the map only after paint, and would not render again on its own.
  if (retainedAgendaIdRef.current !== agendaId) {
    retainedAgendaIdRef.current = agendaId;
    retainedIdentityRef.current.clear();
  }

  if (isJoinedListing) {
    pageIdentityByRowId.forEach((identity, rowId) => retainedIdentityRef.current.set(rowId, identity));
  }

  const identityByRowId = isJoinedListing ? retainedIdentityRef.current : pageIdentityByRowId;
  const hasEditableColumns = useMemo(() => Object.values(dataSchema).some((column) => column.isEditable), [dataSchema]);

  const isColumnEditableForRow = useCallback(
    (row: object, columnId: string) => {
      const rowId = 'identifier' in row && typeof row.identifier === 'string' ? row.identifier : '';

      return canEditJoinedCell(dataSchema[columnId], identityByRowId.get(rowId) ?? row);
    },
    [dataSchema, identityByRowId]
  );

  const { handleCalculateRow, calculatedValues, calculatedVisibility, clearCalculatedValues } = useAgendaTableCalculation({ dataSchema });

  const syncParentCell = useCallback(
    (ref: MutableRefObject<TableRef | null>, rowId: string, columnId: string) => {
      if (!isJoinedListing) return;

      const formValues = ref.current?.getValues();
      const sourceRow = formValues?.[rowId];
      if (!sourceRow || !(columnId in sourceRow)) return;

      const sourceValue = sourceRow[columnId];
      joinedParentCellPatches(rowId, columnId, dataSchema, identityByRowId).forEach((siblingRowId) => {
        const siblingRow = formValues?.[siblingRowId];

        if (siblingRow && columnId in siblingRow && sameCellValue(siblingRow[columnId], sourceValue)) return;

        ref.current?.setCellValue(siblingRowId, columnId, sourceValue);
      });
    },
    [dataSchema, identityByRowId, isJoinedListing]
  );

  const handleJoinedCalculateRow = useCallback(
    async (ref: MutableRefObject<TableRef | null>, calculatedAgendaId: string, rowId: string, rowData: AttributeType[]) => {
      if (!isJoinedListing) {
        await handleCalculateRow(ref, calculatedAgendaId, rowId, rowData);
        return;
      }

      const valuesBeforeCalculate = ref.current?.getValues()?.[rowId];
      const identity = identityByRowId.get(rowId) ?? emptyIdentity();
      const { attributes, inlineRecordData } = splitJoinedCalculatePayload(rowData, dataSchema, identity);

      await handleCalculateRow(ref, calculatedAgendaId, rowId, attributes, inlineRecordData, identity.parentRecordId);

      const valuesAfterCalculate = ref.current?.getValues()?.[rowId];

      Object.keys(dataSchema).forEach((columnId) => {
        if (dataSchema[columnId]?.joinedColumn) return;
        if (sameCellValue(valuesBeforeCalculate?.[columnId], valuesAfterCalculate?.[columnId])) return;

        syncParentCell(ref, rowId, columnId);
      });
    },
    [dataSchema, handleCalculateRow, identityByRowId, isJoinedListing, syncParentCell]
  );

  useEffect(() => {
    clearCalculatedValues();
  }, [recordsResult, clearCalculatedValues]);

  const filterCalculateRequiredFields = useCallback(
    (rowId: string, result: ValidationResult): ValidationResult => {
      if (!isJoinedListing) return result;

      const identity = identityByRowId.get(rowId);
      const missingFields = result.missingFields.filter((columnId) => {
        const column = dataSchema[columnId];

        if (!column?.joinedColumn) return true;
        if (!column.isEditable) return false;

        return canEditJoinedCell(column, identity);
      });

      return { allFilled: missingFields.length === 0, missingFields };
    },
    [dataSchema, identityByRowId, isJoinedListing]
  );

  const { addedRows, handleOnSaveEdit, handleCancelEditRow, savingData, handleRowsRemoved } = useAgendaTableActions({
    agendaId,
    formItemId: undefined,
    isNestedList: false,
    value: [],
    dataSchema,
    setValue,
    triggerForm,
  });

  const handleJoinedSave = useCallback(
    async (ref: MutableRefObject<TableRef | null>) => {
      const isValid = Boolean(await ref.current?.triggerForm());

      if (!isValid) {
        throw new FormValidationException();
      }

      const formData = ref.current?.getValues();
      const dirtyFields = ref.current?.getFormState().dirtyFields;
      const hasDirtyFields = Boolean(formData && dirtyFields && Object.keys(dirtyFields).length);

      if (!hasDirtyFields) {
        if (addedRows.length) toast.error(t('flow.modulesAndAgendas.form.item.records.unsavedJoinedChanges'));
        return;
      }

      const droppedAttributes: DroppedJoinedAttribute[] = [];
      let requests;
      try {
        requests = buildJoinedSaveRequests(agendaId, dirtyFields ?? {}, formData ?? {}, dataSchema, identityByRowId, droppedAttributes);
      } catch (error) {
        if (error instanceof JoinedValueConflictError) {
          toast.error(t('flow.modulesAndAgendas.form.item.records.conflictingRowValues'));
        }

        throw error;
      }

      const skippedChanges = addedRows.length > 0 || droppedAttributes.length > 0;

      if (!requests.length) {
        if (skippedChanges) toast.error(t('flow.modulesAndAgendas.form.item.records.unsavedJoinedChanges'));
        return;
      }

      try {
        await Promise.all(requests.map((request) => updateWithNestedList(request).unwrap()));

        if (skippedChanges) {
          toast.error(t('flow.modulesAndAgendas.form.item.records.unsavedJoinedChanges'));
          return;
        }

        ref.current?.resetSavedForm();
        [...retainedIdentityRef.current.keys()].forEach((rowId) => {
          if (!pageIdentityByRowId.has(rowId)) retainedIdentityRef.current.delete(rowId);
        });
        toast.success(t('flow.modulesAndAgendas.form.item.records.updateRowsSuccess'));
      } catch (error) {
        toast.error(t('flow.modulesAndAgendas.form.item.records.updateRowsError'));
        throw error;
      }
    },
    [addedRows, agendaId, dataSchema, identityByRowId, pageIdentityByRowId, t, updateWithNestedList]
  );

  return {
    dataSchema,
    hasEditableColumns,
    isColumnEditableForRow,
    calculatedValues,
    calculatedVisibility,
    handleCalculateRow: handleJoinedCalculateRow,
    filterCalculateRequiredFields,
    addedRows,
    handleCancelEditRow,
    handleRowsRemoved,
    isSaving: savingData || isJoinedSaving,
    persistEdits: isJoinedListing ? handleJoinedSave : handleOnSaveEdit,
    syncParentCell,
  };
};
